import React, { useEffect, useState } from 'react';
import { parseFechaLocal } from '../utils/date.utils';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Calendar,
  Phone,
  Mail,
  MapPin,
  Heart,
  BookOpen,
  Church,
  Award,
  Users,
  Clock,
  GraduationCap,
  Sparkles,
  CalendarDays,
  Loader2,
  AlertCircle,
  Shield,
  Crown,
  ArrowRight,
} from 'lucide-react';
import { MembershipsService, type MemberProfile } from '../services/memberships/memberships.service';
import { NotificationToast } from '../components/ui/NotificationToast';
import { useAuthStore } from '../store/auth.store';

export const MemberDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuthStore();
  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ isOpen: boolean; title: string; message: string; type: 'success' | 'error' | 'info' }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      const data = await MembershipsService.getMyProfile();
      setProfile(data);
    } catch (err: any) {
      setError(err.message || 'No se pudo cargar tu perfil');
      setToast({
        isOpen: true,
        title: 'Error',
        message: err.message || 'No se pudo cargar tu perfil',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const calcularEdad = (fechaNacimiento: string) => {
    const hoy = new Date();
    const nacimiento = parseFechaLocal(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    return edad;
  };

  const formatFecha = (fechaStr: string) => {
    const fecha = parseFechaLocal(fechaStr);
    return fecha.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatFechaCorta = (fecha: Date) => {
    return fecha.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="w-10 h-10 text-purple-600 animate-spin" />
          <p className="text-gray-500 text-xs">Cargando tu perfil...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <div className="flex flex-col items-center space-y-4 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500" />
          <h2 className="text-xs font-medium text-gray-900">No se pudo cargar tu perfil</h2>
          <p className="text-gray-500 text-xs">{error || 'Algo salió mal al obtener tu información.'}</p>
          <button
            onClick={loadProfile}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-xs font-medium"
          >
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  const { membresia, datosComplementarios, ministerios, ministeriosLiderados, eventos } = profile;
  const edad = calcularEdad(membresia.fechaNacimiento);

  return (
    <div className="space-y-6">
      {/* Header de bienvenida */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-xs font-medium">¡Hola, {membresia.nombres}!</h1>
              <p className="text-white/80 text-xs mt-1">
                {membresia.genero === 'Femenino' ? 'Bienvenida' : 'Bienvenido'} a tu espacio personal en ICC Apartadó
              </p>
            </div>
          </div>
          {/* Rol del usuario */}
          {authUser?.roles && authUser.roles.length > 0 && (
            <div className="hidden sm:flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2.5 border border-white/20">
              <Shield className="w-5 h-5 text-white/90" />
              <div className="text-right">
                <p className="text-[10px] text-white/60 uppercase tracking-wider font-medium leading-none">Tu rol</p>
                <p className="text-xs font-medium text-white leading-tight mt-0.5">{authUser.roles[0]?.nombre}</p>
              </div>
            </div>
          )}
        </div>
        {/* Rol en móvil */}
        {authUser?.roles && authUser.roles.length > 0 && (
          <div className="sm:hidden mt-4 flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/20 w-fit">
            <Shield className="w-4 h-4 text-white/90" />
            <span className="text-xs font-medium text-white">{authUser.roles[0]?.nombre}</span>
          </div>
        )}
      </div>

      {/* Grid principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna izquierda: Información personal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tarjeta de información personal */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center space-x-2 mb-4">
              <User className="w-5 h-5 text-purple-600" />
              <h2 className="text-xs font-medium text-gray-900">Información Personal</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoItem icon={<User className="w-4 h-4" />} label="Nombre completo" value={membresia.nombreCompleto} />
              <InfoItem icon={<Calendar className="w-4 h-4" />} label="Fecha de nacimiento" value={`${formatFecha(membresia.fechaNacimiento)} (${edad} años)`} />
              <InfoItem icon={<Heart className="w-4 h-4" />} label="Género" value={membresia.genero} />
              <InfoItem icon={<Heart className="w-4 h-4" />} label="Estado civil" value={membresia.estadoCivil} />
              <InfoItem icon={<Phone className="w-4 h-4" />} label="Teléfono" value={membresia.telefono} />
              <InfoItem icon={<Mail className="w-4 h-4" />} label="Correo" value={membresia.email} />
              <InfoItem icon={<MapPin className="w-4 h-4" />} label="Zona / Barrio" value={membresia.zonaBarrio} />
              {authUser?.roles && authUser.roles.length > 0 && (
                <InfoItem
                  icon={<Shield className="w-4 h-4 text-purple-600" />}
                  label="Rol en el sistema"
                  value={authUser.roles[0]?.nombre || ''}
                  highlight
                />
              )}
              {membresia.numeroDocumento && (
                <InfoItem
                  icon={<Award className="w-4 h-4" />}
                  label="Documento"
                  value={`${membresia.tipoDocumento?.toUpperCase() || ''} ${membresia.numeroDocumento}`}
                />
              )}
            </div>
          </div>

          {/* Datos complementarios */}
          {datosComplementarios && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <h2 className="text-xs font-medium text-gray-900">Información Espiritual y Personal</h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoItem
                  icon={<Church className="w-4 h-4" />}
                  label="Bautizado"
                  value={datosComplementarios.esBautizado ? 'Sí' : 'No'}
                  highlight={datosComplementarios.esBautizado}
                />
                {datosComplementarios.esBautizado && datosComplementarios.añoBautismo && (
                  <InfoItem icon={<Calendar className="w-4 h-4" />} label="Año de bautismo" value={String(datosComplementarios.añoBautismo)} />
                )}
                <InfoItem
                  icon={<BookOpen className="w-4 h-4" />}
                  label="Oración de fe"
                  value={datosComplementarios.oracionFe ? 'Sí' : 'No'}
                  highlight={datosComplementarios.oracionFe}
                />
                <InfoItem
                  icon={<Clock className="w-4 h-4" />}
                  label="Tiempo asistiendo"
                  value={datosComplementarios.tiempoAsistencia?.replace(/_/g, ' ') || 'No especificado'}
                />
                {datosComplementarios.nivelEstudios && (
                  <InfoItem icon={<GraduationCap className="w-4 h-4" />} label="Nivel de estudios" value={datosComplementarios.nivelEstudios} />
                )}
                {datosComplementarios.profesion && (
                  <InfoItem icon={<Award className="w-4 h-4" />} label="Profesión" value={datosComplementarios.profesion} />
                )}
                {datosComplementarios.tipoPersonalidad && (
                  <InfoItem icon={<Sparkles className="w-4 h-4" />} label="Personalidad" value={datosComplementarios.tipoPersonalidad} />
                )}
                <InfoItem
                  icon={<Users className="w-4 h-4" />}
                  label="Pertenece a ministerio"
                  value={datosComplementarios.perteneceMinisterio ? 'Sí' : 'No'}
                  highlight={datosComplementarios.perteneceMinisterio}
                />
              </div>

              {/* Habilidades y dones */}
              <div className="mt-4 space-y-3">
                {datosComplementarios.habilidades && datosComplementarios.habilidades.length > 0 && (
                  <TagList label="Habilidades" tags={datosComplementarios.habilidades} color="purple" />
                )}
                {datosComplementarios.dones && datosComplementarios.dones.length > 0 && (
                  <TagList label="Dones" tags={datosComplementarios.dones} color="blue" />
                )}
                {datosComplementarios.estudiosRealizados && datosComplementarios.estudiosRealizados.length > 0 && (
                  <TagList label="Estudios realizados" tags={datosComplementarios.estudiosRealizados} color="green" />
                )}
              </div>
            </div>
          )}

          {/* Tus Ministerios — unificado: lidera + pertenece */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Church className="w-5 h-5 text-purple-600" />
                <h2 className="text-xs font-medium text-gray-900">Tus Ministerios</h2>
              </div>
              {(ministeriosLiderados?.length || 0) + (ministerios?.length || 0) > 0 && (
                <span className="text-xs text-gray-400 font-medium">
                  {(ministeriosLiderados?.length || 0) + (ministerios?.length || 0)} en total
                </span>
              )}
            </div>

            {(ministeriosLiderados?.length || 0) === 0 && (ministerios?.length || 0) === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Church className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-xs">No tienes ministerios asignados actualmente</p>
                <p className="text-xs text-gray-300 mt-1">Contacta a un administrador para más información</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Ministerios que lidera */}
                {ministeriosLiderados?.map((min) => (
                  <div key={min.id} className="relative flex items-start space-x-3 p-4 rounded-xl border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Crown className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 text-xs">{min.nombre}</h3>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider bg-amber-100 text-amber-700 rounded-full border border-amber-200">
                          <Crown className="w-3 h-3" /> Líder
                        </span>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${
                          min.estado ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'
                        }`}>
                          {min.estado ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                      {min.descripcion && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{min.descripcion}</p>}
                      <button
                        onClick={() => navigate('/dashboard/iglesia/ministerios')}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-700 transition-colors"
                      >
                        Gestionar ministerio <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Ministerios donde es miembro */}
                {ministerios?.map((min) => (
                  <div key={min.id} className="flex items-start space-x-3 p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Church className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 text-xs">{min.nombre}</h3>
                        {min.rol && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700 border border-purple-200">
                            {min.rol}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 mt-1 block">
                        Miembro desde {formatFechaCorta(parseFechaLocal(min.fechaUnion))}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Columna derecha: Eventos y resumen */}
        <div className="space-y-6">
          {/* Estado del miembro */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Award className="w-5 h-5 text-purple-600" />
              <h2 className="text-xs font-medium text-gray-900">Estado</h2>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                membresia.estado === 'activo'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {membresia.estado === 'activo' ? 'Miembro Activo' : membresia.estado}
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Miembro desde {formatFechaCorta(new Date(membresia.creadoEn))}
            </p>
          </div>

          {/* Próximos eventos */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center space-x-2 mb-4">
              <CalendarDays className="w-5 h-5 text-purple-600" />
              <h2 className="text-xs font-medium text-gray-900">Tus Próximos Eventos</h2>
            </div>

            {eventos.length === 0 ? (
              <div className="text-center py-6 text-gray-400">
                <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-xs">No tienes eventos confirmados</p>
              </div>
            ) : (
              <div className="space-y-3">
                {eventos.map((evt) => (
                  <div key={evt.id} className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex items-start justify-between">
                      <h3 className="font-medium text-gray-900 text-xs flex-1 pr-2">{evt.titulo}</h3>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 flex-shrink-0">
                        {formatFechaCorta(parseFechaLocal(evt.fecha))}
                      </span>
                    </div>
                    {evt.lugar && (
                      <p className="text-xs text-gray-500 mt-1 flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        {evt.lugar}
                      </p>
                    )}
                    {(evt.horaInicio || evt.horaFin) && (
                      <p className="text-xs text-gray-400 mt-1 flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {evt.horaInicio || '--:--'} - {evt.horaFin || '--:--'}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toast */}
      <NotificationToast
        isOpen={toast.isOpen}
        title={toast.title}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

// Componentes auxiliares
const InfoItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}> = ({ icon, label, value, highlight }) => (
  <div className="flex items-start space-x-3 p-3 rounded-xl bg-gray-50/50">
    <div className={`mt-0.5 ${highlight ? 'text-purple-600' : 'text-gray-400'}`}>
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-400 uppercase tracking-wide">{label}</p>
      <p className={`text-xs font-medium mt-0.5 truncate ${highlight ? 'text-purple-700' : 'text-gray-900'}`}>
        {value}
      </p>
    </div>
  </div>
);

const TagList: React.FC<{
  label: string;
  tags: string[];
  color: 'purple' | 'blue' | 'green';
}> = ({ label, tags, color }) => {
  const colorClasses = {
    purple: 'bg-purple-50 text-purple-700 border-purple-100',
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    green: 'bg-green-50 text-green-700 border-green-100',
  };

  return (
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag, i) => (
          <span
            key={i}
            className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${colorClasses[color]}`}
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
};

export default MemberDashboardPage;
